import { useCallback, useMemo } from 'react';

import { useTheme } from '@mui/material';
import { ColorType, Point } from 'highcharts';
import Highcharts from 'highcharts/highstock';

import { TimeSeriesPointCustom } from '@/components/Chart/utils/buildChartLineSeries';
import { createTooltip } from '@/components/Chart/utils/createTooltip';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';

// Constants for styling and layout
const STYLES = {
  container: 'display: flex; flex-direction: column; width:350px;',
  headerRow: 'font-size: 8px; display: flex; font-weight: 400;justify-content: space-between;',
  bodyRow:
    'font-size: 10px; font-style: normal;font-weight: 500;justify-content: space-between;height: 19px; display: flex;',
  nameColumn:
    'width: 152px; padding-right: 8px; border-right: 1px solid gray; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;',
  periodColumn: 'width: 65px; padding-left: 8px; padding-right: 8px;',
  dateColumn: 'width: 65px; padding-left: 8px; border-left: 1px solid gray;',
  valueContainer: 'height 19px; padding-left:8px; padding-top:3px',
  valueBox:
    'font-size:10px; line-height:13px; height:14px; width:55px; padding-left:4px; padding-right:4px; text-align: start;'
} as const;

const COLUMN_WIDTHS = {
  name: 152,
  period: 65,
  date: 65,
  value: 55
} as const;

interface TooltipColumn {
  content: string;
  style: string;
}

const useSpotTooltipFormatter = (label = 'Short Title') => {
  const theme = useTheme();
  const { formatDateString } = useDateText();

  // Memoize utility functions that don't depend on props/state
  const createColumn = useCallback(
    (content: string, style: string): string => `<div style="${style}">${content}</div>`,
    []
  );

  const createValueBox = useCallback(
    (color: ColorType, backgroundColor: ColorType, value: string): string =>
      `<div style="background-color:${backgroundColor}; color:${color}; ${STYLES.valueBox}">${value}</div>`,
    []
  );

  // Memoize header columns based on label
  const headerColumns = useMemo(
    (): TooltipColumn[] => [
      { content: label, style: `width: ${COLUMN_WIDTHS.name}px;` },
      { content: 'PeriodAbs', style: `width: ${COLUMN_WIDTHS.period}px;` },
      { content: 'Last date', style: `width: ${COLUMN_WIDTHS.date}px;` },
      { content: '', style: 'width: 10%;' }
    ],
    [label]
  );

  // Memoize header creation
  const createHeader = useCallback((): string => {
    const columns = headerColumns
      .map(({ content, style }) => createColumn(content, style))
      .join('');

    return `<div style="${STYLES.container}"><div style="${STYLES.headerRow}">${columns}</div>`;
  }, [headerColumns, createColumn]);

  // Memoize body row creation function
  const createBodyRow = useCallback(
    (point: Point, custom: TimeSeriesPointCustom, value: string): string => {
      const nameColumn = createColumn(point.series.name, STYLES.nameColumn);
      const periodColumn = createColumn(custom?.periodAbs ?? '', STYLES.periodColumn);

      const date = formatDateString(new Date(point.x).toJSON(), DateTextVariants.FULL);
      const dateColumn = createColumn(date, STYLES.dateColumn);

      const valueBox = createValueBox(
        theme.palette.background.default,
        point.series.color ?? '#F8F8F8',
        value
      );
      const valueContainer = createColumn(valueBox, STYLES.valueContainer);

      return `<div style="${STYLES.bodyRow}">${nameColumn}${periodColumn}${dateColumn}${valueContainer}</div>`;
    },
    [createColumn, createValueBox, formatDateString, theme.palette.background.default]
  );

  return useCallback(
    (allChartSeries: Highcharts.Series[], hoveredPointXValue: string | number | undefined) => {
      return createTooltip(allChartSeries, hoveredPointXValue, createHeader, createBodyRow);
    },
    [createHeader, createBodyRow]
  );
};

export default useSpotTooltipFormatter;
